/**
 * What the closure form needs and the browser does not give: a wall-clock
 * time read in the planner's zone, and a rectangle typed as four numbers.
 *
 * A dispatcher types "06:00" and means 06:00 at the factory, in
 * `planner_config.local_time_zone` (the API sends it as `time_zone`), not in
 * whatever zone the laptop is set to. `<input type="datetime-local">` hands
 * over the wall clock with no zone at all, so it is turned into an instant
 * here, with its offset, and the API refuses a time without one.
 *
 * The rectangle can be drawn on the map, and can always be typed: drawing is
 * a pointer gesture, and the four coordinate fields are the way in for a
 * keyboard (WCAG 2.1.1, 2.5.7). The checks mirror the API's
 * (`api/app/services/closures.py`) so a mistake is named at the field before
 * anything is sent; the API's answer is still the one that counts.
 */

export interface Area {
  south: number;
  west: number;
  north: number;
  east: number;
}

export type AreaFields = Record<keyof Area, string>;

/** TomTom Routing avoidAreas: at most about 160 km a side (the API's own
 *  limit, `MAX_SIDE_KM`). */
export const MAX_SIDE_KM = 160;
const KM_PER_DEGREE = 111.32;

const WALL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

const offsetFormats = new Map<string, Intl.DateTimeFormat>();

/** The zone's offset from UTC at an instant, in minutes. */
function offsetMinutes(epochMs: number, timeZone: string): number {
  let format = offsetFormats.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    offsetFormats.set(timeZone, format);
  }
  const p: Record<string, number> = {};
  for (const part of format.formatToParts(new Date(epochMs))) p[part.type] = Number(part.value);
  const asUtc = Date.UTC(p.year!, p.month! - 1, p.day!, p.hour!, p.minute!, p.second!);
  return Math.round((asUtc - epochMs) / 60_000);
}

const pad = (n: number) => String(Math.abs(n)).padStart(2, "0");

function isoWithOffset(epochMs: number, offset: number): string {
  const wall = new Date(epochMs + offset * 60_000);
  const sign = offset < 0 ? "-" : "+";
  return (
    `${wall.getUTCFullYear()}-${pad(wall.getUTCMonth() + 1)}-${pad(wall.getUTCDate())}` +
    `T${pad(wall.getUTCHours())}:${pad(wall.getUTCMinutes())}:00` +
    `${sign}${pad(Math.floor(Math.abs(offset) / 60))}:${pad(Math.abs(offset) % 60)}`
  );
}

/**
 * `2026-10-01T06:00` in `Europe/London` as `2026-10-01T06:00:00+01:00`, or
 * null for anything that is not a date and a time.
 *
 * The two days a year the clocks change are decided, not left to chance. A
 * time that happens twice (01:30 on the last Sunday of October) is read as
 * the later one, which is the longer reading for an end and the safer one for
 * a closure. A time that does not happen (01:30 on the last Sunday of March)
 * is moved forward by the gap, as a clock that jumped would show it.
 */
export function wallToInstant(wall: string, timeZone: string): string | null {
  const m = WALL.exec(wall);
  if (!m) return null;
  const [year, month, day, hour, minute] = m.slice(1).map(Number) as [number, number, number, number, number];
  const naive = Date.UTC(year, month - 1, day, hour, minute);
  if (Number.isNaN(naive)) return null;
  // The instants whose wall clock in the zone could be this one: one per
  // offset in force within a day either side.
  const candidates = new Set([
    offsetMinutes(naive - 86_400_000, timeZone),
    offsetMinutes(naive, timeZone),
    offsetMinutes(naive + 86_400_000, timeZone),
  ]);
  const matching = [...candidates]
    .map((offset) => naive - offset * 60_000)
    .filter((instant) => instant + offsetMinutes(instant, timeZone) * 60_000 === naive)
    .sort((a, b) => b - a);
  if (matching.length > 0) {
    const instant = matching[0]!;
    return isoWithOffset(instant, offsetMinutes(instant, timeZone));
  }
  // In the gap: read with the offset before the change, which lands as far
  // past the change as the time was into the gap.
  const before = offsetMinutes(naive - 86_400_000, timeZone);
  const instant = naive - before * 60_000;
  return isoWithOffset(instant, offsetMinutes(instant, timeZone));
}

/** An instant as the wall clock a `datetime-local` input shows, in the zone. */
export function instantToWall(iso: string, timeZone: string): string {
  const epoch = Date.parse(iso);
  const wall = new Date(epoch + offsetMinutes(epoch, timeZone) * 60_000);
  return (
    `${wall.getUTCFullYear()}-${pad(wall.getUTCMonth() + 1)}-${pad(wall.getUTCDate())}` +
    `T${pad(wall.getUTCHours())}:${pad(wall.getUTCMinutes())}`
  );
}

const NAMES: Record<keyof Area, string> = { south: "South", west: "West", north: "North", east: "East" };
const LIMITS: Record<keyof Area, number> = { south: 90, north: 90, west: 180, east: 180 };

/** Width and height in kilometres; the width along the edge nearest the
 *  equator, where the rectangle is widest. */
export function sideKilometres(area: Area): [number, number] {
  const nearest = area.south * area.north > 0 ? Math.min(Math.abs(area.south), Math.abs(area.north)) : 0;
  const width = (area.east - area.west) * KM_PER_DEGREE * Math.cos((nearest * Math.PI) / 180);
  const height = (area.north - area.south) * KM_PER_DEGREE;
  return [width, height];
}

export function parseArea(fields: AreaFields): { area: Area } | { error: string; field: keyof Area } {
  const area = {} as Area;
  for (const key of ["south", "west", "north", "east"] as const) {
    const raw = fields[key].trim().replace(",", ".");
    if (raw === "") return { error: `${NAMES[key]} is needed`, field: key };
    const value = Number(raw);
    if (!Number.isFinite(value)) {
      return {
        error: `${NAMES[key]} must be a number of degrees, east positive and west negative`,
        field: key,
      };
    }
    if (Math.abs(value) > LIMITS[key]) {
      return { error: `${NAMES[key]} must lie between -${LIMITS[key]} and ${LIMITS[key]}`, field: key };
    }
    area[key] = value;
  }
  if (area.north <= area.south) return { error: "North must be north of south", field: "north" };
  if (area.east <= area.west) return { error: "East must be east of west", field: "east" };
  const [width, height] = sideKilometres(area);
  if (Math.max(width, height) > MAX_SIDE_KM) {
    return {
      error: `The area is about ${Math.round(width)} by ${Math.round(height)} km; TomTom avoids at most ${MAX_SIDE_KM} km a side. Record it as smaller closures.`,
      field: height >= width ? "north" : "east",
    };
  }
  return { area };
}

/** Four decimals, about 10 m: finer than a road, and what a drawn rectangle
 *  is rounded to. */
export function areaFields(area: Area): AreaFields {
  return {
    south: area.south.toFixed(4),
    west: area.west.toFixed(4),
    north: area.north.toFixed(4),
    east: area.east.toFixed(4),
  };
}

const degrees = (value: number, positive: string, negative: string) =>
  `${Math.abs(value).toFixed(4)}° ${value >= 0 ? positive : negative}`;

/** The rectangle in words, for the list and for a reader without the map. */
export function describeArea(area: Area): string {
  const [width, height] = sideKilometres(area);
  return (
    `${degrees(area.south, "N", "S")} to ${degrees(area.north, "N", "S")}, ` +
    `${degrees(area.west, "E", "W")} to ${degrees(area.east, "E", "W")}, ` +
    `about ${Math.max(1, Math.round(width))} by ${Math.max(1, Math.round(height))} km`
  );
}

const momentFormats = new Map<string, Intl.DateTimeFormat>();

/** `Thu 1 Oct 06:00`, in the zone: a closure's from and until in the list. */
export function formatMoment(iso: string, timeZone: string): string {
  let format = momentFormats.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    momentFormats.set(timeZone, format);
  }
  const p: Record<string, string> = {};
  for (const part of format.formatToParts(new Date(iso))) p[part.type] = part.value;
  return `${p.weekday} ${p.day} ${p.month} ${p.hour}:${p.minute}`;
}
